import { useState, useEffect } from 'react';
import { ExternalLink } from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { useInView } from 'react-intersection-observer';
import { Link } from 'react-router-dom';

interface BoostedUser {
  id: string;
  username: string;
  fullName: string;
  avatar_url: string;
  cover_url: string;
  bio: string;
  verified: boolean;
  followers: number;
  website?: string;
  media: {
    id: string;
    url: string;
    type: 'image' | 'video';
    thumbnail?: string;
  }[];
}

interface BoostedProfileProps {
  profiles: BoostedUser[];
}

/****
 * Displays a carousel of boosted user profiles with auto-rotation and manual navigation.
 *
 * Renders a horizontally scrollable carousel of user profile cards, each showing cover image, avatar, name, verification status, username, follow button, bio, website link, follower count, and up to five media previews. The carousel auto-rotates every 15 seconds when visible in the viewport and allows manual selection via indicator dots.
 *
 * @param profiles - Array of user profiles to display in the carousel.
 *
 * @returns The boosted profiles carousel component, or null if no profiles are provided.
 */
export default function BoostedProfile({ profiles }: BoostedProfileProps) {
  const [activeProfileIndex, setActiveProfileIndex] = useState(0);
  const { ref, inView } = useInView({
    threshold: 0.1,
    triggerOnce: false
  });

  // Auto-rotate profiles every 15 seconds, but only when in view
  useEffect(() => {
    if (profiles.length <= 1 || !inView) return;

    const interval = setInterval(() => {
      setActiveProfileIndex((prev: number) => (prev + 1) % profiles.length);
    }, 15000); // Set to 15 seconds

    return () => clearInterval(interval);
  }, [profiles.length, inView]);

  if (profiles.length === 0) {
    return null;
  }

  return (
    <div ref={ref} className="sticky top-16 bg-white dark:bg-gray-900 rounded-lg shadow-sm overflow-hidden">
      <div className="bg-gradient-to-r from-primary-500 to-secondary-500 py-1.5 px-3">
        <h3 className="text-white text-sm font-semibold">Boosted Profiles</h3>
      </div>

      <div className="relative overflow-hidden">
        <div
          className="transition-transform duration-500 ease-in-out"
          style={{ transform: `translateX(-${activeProfileIndex * 100}%)` }}
        >
          <div className="flex">
            {profiles.map((profile) => (
              <div
                key={profile.id}
                className="w-full flex-shrink-0"
              >
                {/* Cover Image */}
                <div className="relative h-24 w-full">
                  {profile.cover_url ? (
                    <>
                      <div
                        className="absolute inset-0 bg-cover bg-center"
                        style={{ backgroundImage: `url(${profile.cover_url})` }}
                      ></div>
                      <div className="absolute inset-0 bg-gradient-to-b from-transparent to-black/30"></div>
                    </>
                  ) : (
                    <>
                      {/* Ad banner displayed when no cover image is present */}
                      <a href="#" className="block w-full h-full">
                        <img
                          src="/images/ads/summer-banner.jpg"
                          alt="Summer Sale"
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            // Fallback to a Pixabay image if the local image fails to load
                            const target = e.target as HTMLImageElement;
                            target.onerror = null; // Prevent infinite error loop
                            target.src = 'https://cdn.pixabay.com/photo/2020/01/21/11/39/banner-4782921_1280.jpg';
                          }}
                        />
                      </a>
                    </>
                  )}

                  {/* Follow button positioned at bottom right of cover */}
                  <div className="absolute bottom-2 right-2">
                    <Button
                      className="bg-primary-500 hover:bg-primary-600 text-black text-xs py-0.5 px-3 rounded-full h-6"
                    >
                      Follow
                    </Button>
                  </div>
                </div>

                {/* Profile Info Section */}
                <div className="relative px-3 pb-3">
                  {/* Avatar - Half on/half off cover */}
                  <div className="absolute -top-8 left-3">
                    <Link to={`/profile/${profile.username}`}>
                      <Avatar className="w-16 h-16 border-2 border-primary-300 dark:border-primary-600 shadow-md">
                        <AvatarImage src={profile.avatar_url} alt={profile.username} />
                        <AvatarFallback>{profile.username.charAt(0)}</AvatarFallback>
                      </Avatar>
                    </Link>
                  </div>

                  {/* Name and Username - Right of avatar */}
                  <div className="ml-20 pt-1 mb-2">
                    <div className="flex items-center">
                      <Link to={`/profile/${profile.username}`} className="font-semibold text-gray-900 dark:text-white hover:text-primary-600 dark:hover:text-primary-400 transition-colors">
                        {profile.fullName || profile.username}
                      </Link>
                      {profile.verified && (
                        <svg className="w-3.5 h-3.5 ml-1 text-blue-500" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"></path>
                        </svg>
                      )}
                    </div>
                    <p className="text-sm text-gray-500 dark:text-gray-400">@{profile.username}</p>
                  </div>

                  {/* Bio */}
                  <div className="mt-3">
                    <p className="text-xs text-gray-600 dark:text-gray-300 line-clamp-2">
                      {profile.bio}
                    </p>

                    {profile.website && (
                      <a
                        href={profile.website.startsWith('http') ? profile.website : `https://${profile.website}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center mt-1 text-xs text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300"
                      >
                        <ExternalLink className="w-3 h-3 mr-1" />
                        {profile.website.replace(/^https?:\/\//, '').replace(/\/$/, '')}
                      </a>
                    )}

                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      <span className="font-medium">{(profile.followers || 0).toLocaleString()}</span> followers
                    </p>
                  </div>

                  {/* Media Previews - 5 images/videos */}
                  <div className="mt-3">
                    <div className="grid grid-cols-5 gap-0.5">
                      {(profile.media || []).slice(0, 5).map((item) => (
                        <Link
                          key={item.id}
                          to={`/profile/${profile.username}/media/${item.id}`}
                          className="aspect-square rounded-sm overflow-hidden relative group"
                        >
                          <img
                            src={item.type === 'video' && item.thumbnail ? item.thumbnail : item.url}
                            alt=""
                            className="w-full h-full object-cover"
                          />
                          {item.type === 'video' && (
                            <div className="absolute inset-0 flex items-center justify-center">
                              <div className="w-4 h-4 bg-black/50 rounded-full flex items-center justify-center">
                                <div className="w-0 h-0 border-t-transparent border-t-4 border-b-transparent border-b-4 border-l-white border-l-6 ml-0.5"></div>
                              </div>
                            </div>
                          )}
                          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors"></div>
                        </Link>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Indicator dots */}
      {profiles.length > 1 && (
        <div className="flex justify-center space-x-1 pb-2">
          {profiles.map((_, i) => (
            <button
              key={i}
              onClick={() => setActiveProfileIndex(i)}
              className={`w-1.5 h-1.5 md:w-1.5 md:h-1.5 rounded-full ${i === activeProfileIndex ? 'bg-primary-500' : 'bg-gray-300 dark:bg-gray-600'}`}
              aria-label={`Go to profile ${i + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  );
}
